import React from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonList,
  IonButton,
  IonIcon,
  IonText,
  IonNote
} from '@ionic/react';
import { arrowUp, arrowDown } from 'ionicons/icons';
import { IssueFlaggerTable, IssueTriageTable } from './parameters-schema';

interface IssuePriorityEditorProps {
  issueFlaggerTables: IssueFlaggerTable[];
  triageTable: IssueTriageTable;
  onUpdate: (newTriageTable: IssueTriageTable) => void;
}

export const IssuePriorityEditor: React.FC<IssuePriorityEditorProps> = ({
  issueFlaggerTables,
  triageTable,
  onUpdate,
}) => {
  // Create a map of issue IDs to their display titles
  const issueTitlesMap = new Map(
    issueFlaggerTables.map(table => [table.id, table.displayTitle || table.id])
  );

  // Get the set of all current issue IDs
  const currentIssueIds = new Set(issueFlaggerTables.map(table => table.id));

  // Filter out non-existent issues and add new ones
  const getUpdatedOrder = (currentOrder: string[]): string[] => {
    const validOrder = currentOrder.filter(id => currentIssueIds.has(id));
    const newIssues = Array.from(currentIssueIds).filter(id => !validOrder.includes(id));
    return [...validOrder, ...newIssues];
  };

  // Get the current order, filtered and with new issues appended
  const currentOrder = getUpdatedOrder(triageTable.issueFlaggerIds);

  // Move an item up or down in the order
  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...currentOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newOrder.length) {
      [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
      onUpdate({
        ...triageTable,
        issueFlaggerIds: newOrder
      });
    }
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Issue Priority Order</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <IonText color="medium" className="ion-padding-bottom">
          <p>
            If multiple inference rules show 'red light' status for a client, 
            the issues should be addressed in the following order:
          </p>
        </IonText>

        <IonList className="ion-padding-top">
          <IonNote className="ion-padding-start">Most Important</IonNote>
          
          {currentOrder.map((issueId, index) => (
            <IonItem key={issueId}>
              <IonLabel>{issueTitlesMap.get(issueId)}</IonLabel>
              <IonButton
                fill="clear"
                disabled={index === 0}
                onClick={() => moveItem(index, 'up')}
              >
                <IonIcon icon={arrowUp} />
              </IonButton>
              <IonButton
                fill="clear"
                disabled={index === currentOrder.length - 1}
                onClick={() => moveItem(index, 'down')}
              >
                <IonIcon icon={arrowDown} />
              </IonButton>
            </IonItem>
          ))}

          <IonNote className="ion-padding-start">Least Important</IonNote>
        </IonList>
      </IonCardContent>
    </IonCard>
  );
}; 